import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useBrands } from "@/contexts/BrandContext";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { MARKETPLACE_OPTIONS, COUNTRY_OPTIONS } from "@/lib/marketplaces";
import { useCategories } from "@/hooks/useCategories";
import { InfoTip } from "@/components/app/InfoTip";

const EMPTY = {
  brand_name: "", marketplace: "Amazon", country: "India",
  category: "", subcategory: "", positioning_statement: "",
  target_audience: "", price_positioning: "Premium",
  monthly_ad_budget: "", target_acos: "",
  claim_restrictions: "", brand_notes: "",
  categories: [],   // [{category, subcategories: []}]
  bid_ranges: {},   // tier -> [low, high] (empty = use defaults)
};

// Suggested-bid tiers + their hard-coded default INR ranges (kept in sync with
// /app/backend/recommendations/_shared.py:DEFAULT_BID_RANGES). Brand-level
// overrides on these tiers feed straight into bid recommendation generation.
const BID_TIERS = [
  { key: "exact_high",      label: "Exact · high demand",   defaults: [18, 30], help: "Score ≥ 70 exact-match keywords" },
  { key: "exact_medium",    label: "Exact · medium demand", defaults: [12, 18], help: "Mid-tier exact-match keywords" },
  { key: "phrase",          label: "Phrase",                defaults: [10, 18], help: "Phrase-match recommendations" },
  { key: "broad",           label: "Broad",                 defaults: [6, 12],  help: "Broad-match exploration" },
  { key: "competitor_asin", label: "Competitor ASIN",       defaults: [8, 16],  help: "Sponsored Display ASIN targeting" },
  { key: "defensive",       label: "Defensive (own brand)", defaults: [4, 8],   help: "Brand-defense bids" },
];

export default function Brands() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const { refreshBrands } = useBrands();
  const { categories, pricePositioning } = useCategories();

  const load = () => api.get("/brands").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      // Strip empty category rows before save.
      const cats = (form.categories || []).filter((r) => r.category);
      // Strip empty / invalid bid-range overrides — only keep rows where BOTH
      // numbers are valid AND lo <= hi (else send default-empty so backend
      // falls back to DEFAULT_BID_RANGES).
      const cleanRanges = {};
      for (const [k, v] of Object.entries(form.bid_ranges || {})) {
        const lo = v?.[0] === "" || v?.[0] == null ? null : Number(v[0]);
        const hi = v?.[1] === "" || v?.[1] == null ? null : Number(v[1]);
        if (lo == null || hi == null || isNaN(lo) || isNaN(hi)) continue;
        if (lo < 0 || hi < 0) continue;
        if (lo > hi) {
          toast.error(`Bid range for ${k}: low (${lo}) cannot exceed high (${hi})`);
          return;
        }
        cleanRanges[k] = [lo, hi];
      }
      const payload = {
        ...form,
        categories: cats,
        // Mirror primary category to legacy single fields for back-compat
        // with components that still read brand.category / brand.subcategory.
        category: cats[0]?.category || form.category || null,
        subcategory: cats[0]?.subcategories?.[0] || null,
        monthly_ad_budget: form.monthly_ad_budget ? Number(form.monthly_ad_budget) : null,
        target_acos: form.target_acos ? Number(form.target_acos) : null,
        bid_ranges: Object.keys(cleanRanges).length ? cleanRanges : null,
      };
      if (editing) await api.put(`/brands/${editing.id}`, payload);
      else await api.post("/brands", payload);
      toast.success(editing ? "Brand updated — rematching uploads in background" : "Brand created");
      setOpen(false); setEditing(null); setForm(EMPTY);
      await load(); await refreshBrands();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const onEdit = (b) => {
    // Migrate legacy single-category brands into the new categories[] shape
    // so the form always shows the multi-row builder.
    const cats = (b.categories && b.categories.length) ? b.categories
      : (b.category ? [{ category: b.category, subcategories: b.subcategory ? [b.subcategory] : [] }] : []);
    setEditing(b);
    setForm({ ...EMPTY, ...b, categories: cats, bid_ranges: b.bid_ranges || {} });
    setOpen(true);
  };

  // ---------- categories[] helpers ----------
  const addCategoryRow = () =>
    setForm((f) => ({ ...f, categories: [...(f.categories || []), { category: "", subcategories: [] }] }));
  const removeCategoryRow = (idx) =>
    setForm((f) => ({ ...f, categories: f.categories.filter((_, i) => i !== idx) }));
  const setCategoryAt = (idx, cat) =>
    setForm((f) => ({ ...f, categories: f.categories.map((r, i) => i === idx ? { category: cat, subcategories: [] } : r) }));
  const toggleSubcat = (idx, sub) =>
    setForm((f) => ({
      ...f,
      categories: f.categories.map((r, i) => {
        if (i !== idx) return r;
        const has = r.subcategories.includes(sub);
        return { ...r, subcategories: has ? r.subcategories.filter((s) => s !== sub) : [...r.subcategories, sub] };
      }),
    }));

  const [suggesting, setSuggesting] = useState(false);
  const suggestCategories = async () => {
    setSuggesting(true);
    try {
      const body = {};
      if (editing?.id) body.brand_id = editing.id;
      // Even for new brands we send the typed name so the categories master
      // tokenizer can score weak matches (e.g. "Glow Sunscreen" → Beauty › Skin Care).
      if (form.brand_name) body.product_names = [form.brand_name];
      const r = await api.post("/brands/suggest-categories", body);
      const sug = r.data?.suggestions || [];
      if (!sug.length) {
        toast.info("No category suggestions found — try uploading data first or pick manually.");
        return;
      }
      // Merge top suggestions into form.categories WITHOUT overwriting what's
      // there. The user can remove anything they don't want.
      const existingNames = new Set((form.categories || []).map((r) => r.category));
      const merged = [...(form.categories || [])];
      for (const s of sug) {
        if (existingNames.has(s.category)) continue;
        merged.push({ category: s.category, subcategories: s.subcategories || [] });
      }
      setForm((f) => ({ ...f, categories: merged }));
      toast.success(`Added ${sug.length} suggestion${sug.length === 1 ? "" : "s"} — review & save.`);
    } catch (e) { toast.error(extractError(e)); }
    finally { setSuggesting(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Masters</p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Brands</h1>
          <p className="mt-1 text-sm text-slate-500">Configure brand profile, positioning, and ad targets.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(EMPTY); } }}>
          <DialogTrigger asChild>
            <Button data-testid="brand-add-btn" className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg">
              <Plus className="h-4 w-4 mr-1" /> New brand
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit brand" : "Create brand"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <Field label="Brand name" required>
                <Input data-testid="brand-form-name" value={form.brand_name} onChange={(e) => setForm({ ...form, brand_name: e.target.value })} />
              </Field>
              <Field label="Marketplace">
                <Select value={form.marketplace} onValueChange={(v) => setForm({ ...form, marketplace: v })}>
                  <SelectTrigger data-testid="brand-form-marketplace"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MARKETPLACE_OPTIONS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Country">
                <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                  <SelectTrigger data-testid="brand-form-country"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COUNTRY_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label={
                <span className="inline-flex items-center gap-1">
                  Categories &amp; subcategories <InfoTip term="BRAND_CATEGORIES" />
                </span>
              } full>
                <p className="text-[11px] text-slate-500 mb-2">
                  Add every category this brand operates in. Search terms whose detected cluster falls outside these categories will be flagged <span className="font-semibold text-rose-600">out of brand scope</span> on the Search Terms page.
                </p>
                <div className="space-y-2" data-testid="brand-form-categories">
                  {(form.categories || []).map((row, idx) => {
                    const cat = categories.find((c) => c.category_name === row.category);
                    const subs = cat?.subcategories || [];
                    return (
                      <div key={idx} className="rounded-lg border border-slate-200 p-3 bg-slate-50/50" data-testid={`brand-cat-row-${idx}`}>
                        <div className="flex items-center gap-2">
                          <Select value={row.category} onValueChange={(v) => setCategoryAt(idx, v)}>
                            <SelectTrigger data-testid={`brand-cat-${idx}-select`} className="bg-white"><SelectValue placeholder="Pick a category" /></SelectTrigger>
                            <SelectContent className="max-h-72">
                              {categories.map((c) => <SelectItem key={c.id} value={c.category_name}>{c.category_name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Button type="button" variant="ghost" size="sm" data-testid={`brand-cat-${idx}-remove`} onClick={() => removeCategoryRow(idx)} className="text-rose-600 hover:bg-rose-50 shrink-0">
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        {!!subs.length && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {subs.map((s) => {
                              const has = row.subcategories.includes(s);
                              return (
                                <button
                                  key={s} type="button"
                                  data-testid={`brand-cat-${idx}-sub-${s}`}
                                  onClick={() => toggleSubcat(idx, s)}
                                  className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${has ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"}`}
                                >
                                  {s}
                                </button>
                              );
                            })}
                          </div>
                        )}
                        {!subs.length && row.category && (
                          <div className="mt-2 text-[11px] text-slate-500 italic">All subcategories under {row.category} will be considered in-scope.</div>
                        )}
                      </div>
                    );
                  })}
                  {!form.categories?.length && (
                    <div className="text-xs text-slate-500 italic">No categories yet — click "Add category" to start. Leaving this empty disables auto-classification.</div>
                  )}
                  <Button type="button" variant="outline" size="sm" data-testid="brand-form-add-category" onClick={addCategoryRow} className="gap-1.5 mt-1">
                    <Plus className="h-3 w-3" /> Add category
                  </Button>
                  <Button
                    type="button" variant="outline" size="sm"
                    data-testid="brand-form-suggest-categories"
                    onClick={suggestCategories}
                    disabled={suggesting || (!editing && !form.brand_name)}
                    className="gap-1.5 mt-1 ml-2 text-violet-700 border-violet-200 hover:bg-violet-50"
                    title={editing ? "Suggest from this brand's recent search-term data + brand name" : "Suggest from the brand name (upload data after to refine)"}
                  >
                    <Sparkles className="h-3 w-3" /> {suggesting ? "Analysing…" : "Suggest categories"}
                  </Button>
                </div>
              </Field>
              <Field label={<span className="inline-flex items-center gap-1">Price positioning <InfoTip term="PRICE_POSITIONING" /></span>}>
                <Select value={form.price_positioning || ""} onValueChange={(v) => setForm({ ...form, price_positioning: v })}>
                  <SelectTrigger data-testid="brand-form-price-positioning"><SelectValue placeholder="Select tier" /></SelectTrigger>
                  <SelectContent>
                    {pricePositioning.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Monthly ad budget (₹)">
                <Input type="number" value={form.monthly_ad_budget || ""} onChange={(e) => setForm({ ...form, monthly_ad_budget: e.target.value })} />
              </Field>
              <Field label={<span className="inline-flex items-center gap-1">Target ACOS (%) <InfoTip term="ACOS" /></span>}>
                <Input type="number" value={form.target_acos || ""} onChange={(e) => setForm({ ...form, target_acos: e.target.value })} />
              </Field>
              <Field label="Positioning statement" full>
                <Textarea value={form.positioning_statement || ""} onChange={(e) => setForm({ ...form, positioning_statement: e.target.value })} />
              </Field>
              <Field label="Target audience" full>
                <Textarea value={form.target_audience || ""} onChange={(e) => setForm({ ...form, target_audience: e.target.value })} />
              </Field>
              <Field label="Claim restrictions" full>
                <Textarea value={form.claim_restrictions || ""} onChange={(e) => setForm({ ...form, claim_restrictions: e.target.value })} />
              </Field>
              <BidRangeOverrides form={form} setForm={setForm} />
            </div>
            <DialogFooter>
              <Button data-testid="brand-form-save" onClick={save} className="bg-slate-900 text-white hover:bg-slate-800">
                {editing ? "Save" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="brands-list">
        {items.map((b) => (
          <div key={b.id} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 tracking-tight">{b.brand_name}</h3>
                <div className="text-xs text-slate-500 mt-0.5">{b.marketplace} · {b.country}</div>
              </div>
              <Button variant="ghost" size="sm" data-testid={`brand-edit-${b.brand_name}`} onClick={() => onEdit(b)}>
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {(b.categories && b.categories.length
                ? b.categories.flatMap((row) => [
                    row.category,
                    ...(row.subcategories || []),
                  ])
                : [b.category, b.subcategory]
              ).filter(Boolean).slice(0, 6).map((label, i) => (
                <Tag key={i}>{label}</Tag>
              ))}
              {b.price_positioning ? <Tag>{b.price_positioning}</Tag> : null}
            </div>
            {b.positioning_statement ? (
              <p className="mt-4 text-sm text-slate-600 line-clamp-3">{b.positioning_statement}</p>
            ) : null}
            <div className="mt-4 grid grid-cols-2 gap-2 text-[11px] text-slate-500">
              <div><span className="text-slate-400">Ad budget</span><br /><span className="font-semibold text-slate-900">₹{(b.monthly_ad_budget || 0).toLocaleString()}</span></div>
              <div><span className="text-slate-400">Target ACOS</span><br /><span className="font-semibold text-slate-900">{b.target_acos || 0}%</span></div>
            </div>
          </div>
        ))}
        {!items.length && <div className="text-sm text-slate-500">No brands yet — create your first.</div>}
      </div>
    </div>
  );
}

function Field({ label, required, full, children }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">
        {label} {required ? <span className="text-rose-500">*</span> : null}
      </Label>
      {children}
    </div>
  );
}

function Tag({ children }) {
  return (
    <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-[11px] font-semibold tracking-wide text-slate-600">
      {children}
    </span>
  );
}

function BidRangeOverrides({ form, setForm }) {
  const ranges = form.bid_ranges || {};
  const setTier = (key, idx, val) =>
    setForm((f) => {
      const cur = (f.bid_ranges || {})[key] || ["", ""];
      const next = [...cur];
      next[idx] = val;
      return { ...f, bid_ranges: { ...(f.bid_ranges || {}), [key]: next } };
    });
  const clearTier = (key) =>
    setForm((f) => {
      const next = { ...(f.bid_ranges || {}) };
      delete next[key];
      return { ...f, bid_ranges: next };
    });
  const overriddenCount = BID_TIERS.filter((t) => {
    const v = ranges[t.key];
    return v && (v[0] !== "" || v[1] !== "");
  }).length;

  return (
    <div className="col-span-2" data-testid="brand-form-bid-ranges">
      <div className="flex items-center justify-between mb-2">
        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 inline-flex items-center gap-1">
          Bid range overrides (₹) <InfoTip term="CPC" title="Per-brand bid ranges" text="Override the default INR bid ranges that the recommendation engine emits for each tier. Leave blank to keep platform defaults. Stored on the brand and applied on every reco regen." />
        </Label>
        <span className="text-[10px] text-slate-500">
          {overriddenCount === 0 ? "Using platform defaults" : `${overriddenCount} tier${overriddenCount === 1 ? "" : "s"} overridden`}
        </span>
      </div>
      <div className="rounded-lg border border-slate-200 bg-slate-50/50 divide-y divide-slate-100">
        {BID_TIERS.map((t) => {
          const v = ranges[t.key] || ["", ""];
          const isOverridden = v[0] !== "" || v[1] !== "";
          return (
            <div key={t.key} className="grid grid-cols-12 gap-2 px-3 py-2 items-center text-xs" data-testid={`bid-tier-row-${t.key}`}>
              <div className="col-span-5">
                <div className="font-semibold text-slate-800">{t.label}</div>
                <div className="text-[10px] text-slate-500">{t.help} · default ₹{t.defaults[0]}–{t.defaults[1]}</div>
              </div>
              <div className="col-span-3">
                <Input
                  type="number" step="0.5" min="0"
                  data-testid={`bid-tier-${t.key}-low`}
                  placeholder={String(t.defaults[0])}
                  value={v[0]}
                  onChange={(e) => setTier(t.key, 0, e.target.value)}
                  className="h-7 text-xs"
                />
              </div>
              <div className="col-span-3">
                <Input
                  type="number" step="0.5" min="0"
                  data-testid={`bid-tier-${t.key}-high`}
                  placeholder={String(t.defaults[1])}
                  value={v[1]}
                  onChange={(e) => setTier(t.key, 1, e.target.value)}
                  className="h-7 text-xs"
                />
              </div>
              <div className="col-span-1 text-right">
                {isOverridden && (
                  <Button
                    type="button" variant="ghost" size="sm"
                    data-testid={`bid-tier-${t.key}-clear`}
                    onClick={() => clearTier(t.key)}
                    className="h-6 w-6 p-0 text-slate-500 hover:text-rose-600"
                    title="Reset to platform default"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-1.5 text-[10px] text-slate-500">
        Re-run "Regenerate" on the Bidding page after saving to apply these overrides to recommendations.
      </p>
    </div>
  );
}
